/**
 * Start a new owner at this customer's property (RUT-1455).
 *
 * When a home is sold, the office should not have to measure it again — the lawn
 * did not change, only the person paying for it. This starts a brand-new customer
 * at the same property, carrying the property details and the measurement drawings
 * across, and nothing about the current customer: no contacts, no balance, no
 * payment methods, no services, no history.
 *
 * Self-contained on purpose — the customer detail page is already very large, so
 * this owns its own dialog state, mutation and navigation.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { House } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { CreateCustomerModal } from '@/components/customers/CreateCustomerModal';
import { useCreateCustomerMutation } from '@/hooks/useMutations';
import { usePermission } from '@/hooks/usePermission';
import { toast } from '@/components/ui/use-toast';
import { CustomerFormData } from '@/lib/types';
import { CustomerSource, CustomerStatus, PhoneType, Permission, getCustomerDisplayName } from '@ruta/shared';
import type { Customer } from '@/lib/types';
import { cn } from '@/lib/utils';

interface NewOwnerAtPropertyButtonProps {
  customer: Customer;
  className?: string;
  /**
   * Controlled open. When set with `onOpenChange`, the parent owns dialog state
   * (e.g. a customer-name menu item). Uncontrolled by default.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Hide the default header button. Use when the dialog is opened from elsewhere
   * (menu item, etc.).
   */
  hideTrigger?: boolean;
}

function emptyFormFor(customer: Customer): CustomerFormData {
  return {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    phoneType: PhoneType.MOBILE,
    // The server takes the address from the property being copied; this is here so
    // the form has something to show and validate against.
    address: customer.address || '',
    status: CustomerStatus.PROSPECT,
    source: CustomerSource.MANUAL,
    notes: '',
    copyPropertyFromCustomerId: customer.id,
  } as CustomerFormData;
}

export function NewOwnerAtPropertyButton({
  customer,
  className,
  open: openProp,
  onOpenChange,
  hideTrigger = false,
}: NewOwnerAtPropertyButtonProps) {
  const navigate = useNavigate();
  const canManageCustomers = usePermission(Permission.CUSTOMERS_MANAGE);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : uncontrolledOpen;
  const [formData, setFormData] = useState<CustomerFormData>(() => emptyFormFor(customer));
  const createCustomer = useCreateCustomerMutation();

  if (!canManageCustomers) return null;

  const handleOpenChange = (next: boolean) => {
    if (!controlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
    // Reset on close so a second sale at the same property starts clean.
    if (!next) {
      setFormData(emptyFormFor(customer));
      createCustomer.reset();
    }
  };

  const handleSave = async () => {
    const result = await createCustomer.mutateAsync(formData);
    const newCustomer = result.customer;

    handleOpenChange(false);
    setFormData(emptyFormFor(customer));

    toast({
      title: 'New owner started',
      description: `${getCustomerDisplayName(newCustomer)} has the property measurements. Their services start fresh.`,
    });

    navigate(`/customers/${newCustomer.id}`);
  };

  return (
    <>
      {!hideTrigger && (
        <Button
          size="sm"
          variant="ghost"
          className={cn('text-white/80 hover:text-white hover:bg-white/10 h-8 px-2.5', className)}
          onClick={() => handleOpenChange(true)}
        >
          <House className="h-4 w-4 mr-1.5" />
          <span className="text-xs">Start new owner here</span>
        </Button>
      )}

      <CreateCustomerModal
        open={open}
        onOpenChange={handleOpenChange}
        formData={formData}
        onFormDataChange={(updates) => setFormData((prev) => ({ ...prev, ...updates }))}
        onSave={handleSave}
        saving={createCustomer.isPending}
        error={createCustomer.error?.message || null}
        propertySource={{
          id: customer.id,
          displayName: getCustomerDisplayName(customer),
          address: customer.address || '',
        }}
      />
    </>
  );
}
